import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BUYER_TYPES } from "@/lib/constants";

/**
 * Reusable contact + consent block used on every lead form.
 * Controlled via parent `values` + `onChange(field, value)`.
 */
export default function LeadFields({
  values,
  onChange,
  showRole = false,
  consentLabel = "I agree to receive relevant PFAS / brownfield risk information from PFAS Risk Intelligence.",
  idPrefix = "lead",
}) {
  const set = (k) => (e) =>
    onChange(k, e && e.target ? e.target.value : e);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Field label="First name" required>
        <Input value={values.first_name || ""} onChange={set("first_name")}
          data-testid={`${idPrefix}-first-name`} required autoComplete="given-name" />
      </Field>
      <Field label="Last name" required>
        <Input value={values.last_name || ""} onChange={set("last_name")}
          data-testid={`${idPrefix}-last-name`} required autoComplete="family-name" />
      </Field>
      <Field label="Work email" required>
        <Input type="email" value={values.email || ""} onChange={set("email")}
          data-testid={`${idPrefix}-email`} required autoComplete="email" />
      </Field>
      <Field label="Mobile" required>
        <Input type="tel" value={values.mobile || ""} onChange={set("mobile")}
          data-testid={`${idPrefix}-mobile`} required autoComplete="tel" />
      </Field>
      <Field label="Company" required>
        <Input value={values.company || ""} onChange={set("company")}
          data-testid={`${idPrefix}-company`} required autoComplete="organization" />
      </Field>
      <Field label="Buyer type" required>
        <Select value={values.buyer_type || ""} onValueChange={(v) => onChange("buyer_type", v)}>
          <SelectTrigger data-testid={`${idPrefix}-buyer-type`} className="bg-[#12141A] border-white/10 rounded-none h-11">
            <SelectValue placeholder="Select buyer type" />
          </SelectTrigger>
          <SelectContent className="bg-[#12141A] border-white/10 text-white">
            {BUYER_TYPES.map((b) => (
              <SelectItem key={b} value={b} data-testid={`${idPrefix}-buyer-type-${b.replace(/\s+/g,'-').toLowerCase()}`}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {showRole && (
        <Field label="Role" className="md:col-span-2">
          <Input value={values.role || ""} onChange={set("role")}
            data-testid={`${idPrefix}-role`} placeholder="e.g. Development Director, Planning Manager, Estates Solicitor" />
        </Field>
      )}
      <div className="md:col-span-2 flex items-start gap-3 pt-2">
        <Checkbox
          id={`${idPrefix}-consent`}
          checked={!!values.consent}
          onCheckedChange={(v) => onChange("consent", !!v)}
          data-testid={`${idPrefix}-consent`}
          className="mt-1 border-white/30 data-[state=checked]:bg-white data-[state=checked]:text-[#0A0B0E]"
        />
        <label htmlFor={`${idPrefix}-consent`} className="text-sm text-white/70 leading-relaxed cursor-pointer">
          {consentLabel}
        </label>
      </div>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      <Label className="text-[11px] font-mono-alt tracking-[0.18em] uppercase text-white/60">
        {label}{required && <span className="text-white/40 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

export { Field };
